// Prose.js — typography for long-form pages (articles, records): page header,
// section headings, paragraphs, lists, internal links, and a call-to-action
// row. Keeps every content page on one readable, consistent style.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Container, Typography } from "@mui/material";
import { BRAND } from "../theme";

// `wide` for pages built around a wide table (the text inside should cap its
// own line length).
export function ProsePage({ children, wide = false }) {
  return (
    <Container maxWidth={wide ? "lg" : "md"} sx={{ py: { xs: 3, sm: 5 }, position: "relative", zIndex: 1 }}>
      {children}
    </Container>
  );
}

export function PageHeader({ eyebrow, title, lede, meta }) {
  return (
    <Box component="header" sx={{ mb: { xs: 3, sm: 4 } }}>
      {eyebrow && (
        <Typography sx={{ color: BRAND.cyan, fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", mb: 1.25 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography
        component="h1"
        sx={{ color: "#fff", fontSize: { xs: 30, sm: 42 }, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, mb: 1.5 }}
      >
        {title}
      </Typography>
      {meta && <Typography sx={{ color: BRAND.textSecondary, fontSize: 13.5, mb: 2 }}>{meta}</Typography>}
      {lede && (
        <Typography sx={{ color: "#e6e8ee", fontSize: { xs: 17, sm: 19 }, lineHeight: 1.6, fontWeight: 500 }}>{lede}</Typography>
      )}
    </Box>
  );
}

export function H2({ children, id, sx }) {
  return (
    <Typography
      component="h2"
      id={id}
      sx={{ color: "#fff", fontSize: { xs: 21, sm: 25 }, fontWeight: 800, letterSpacing: "-0.01em", mt: { xs: 4, sm: 5 }, mb: 1.5, ...sx }}
    >
      {children}
    </Typography>
  );
}

export function P({ children }) {
  return <Typography sx={{ color: "#c9ccd6", fontSize: { xs: 16, sm: 17 }, lineHeight: 1.8, mb: 2 }}>{children}</Typography>;
}

export function UL({ children }) {
  return (
    <Box component="ul" sx={{ color: "#c9ccd6", fontSize: { xs: 16, sm: 17 }, lineHeight: 1.8, pl: 3, mb: 2, "& li": { mb: 0.75 } }}>
      {children}
    </Box>
  );
}

export function Strong({ children }) {
  return (
    <Box component="strong" sx={{ color: "#fff", fontWeight: 700 }}>
      {children}
    </Box>
  );
}

export function InternalLink({ to, children }) {
  return (
    <Box component={RouterLink} to={to} sx={{ color: BRAND.cyan, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
      {children}
    </Box>
  );
}

export function CtaRow({ links }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 5, pt: 3, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
      {links.map(({ to, label, primary }) => (
        <Button
          key={to}
          component={RouterLink}
          to={to}
          variant={primary ? "contained" : "outlined"}
          sx={{
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 999,
            px: 2.5,
            ...(primary
              ? { background: BRAND.gradient, color: "#fff" }
              : { borderColor: "rgba(255,255,255,0.18)", color: "#e6e8ee", "&:hover": { borderColor: BRAND.cyan } }),
          }}
        >
          {label}
        </Button>
      ))}
    </Box>
  );
}
